import { useCallback, useEffect, useRef, useState } from 'react';
import { projects } from '../data/portfolio.js';
import SectionHeading from './shared/SectionHeading.jsx';

const AUTO_ADVANCE_MS = 6500;
const DRAG_THRESHOLD = 0.18; // fraction of card width to trigger change

export default function Portfolio() {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [paused, setPaused] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);

  const dragState = useRef({
    startX: 0,
    startY: 0,
    lastX: 0,
    velocity: 0,
    pointerId: null,
    decided: false,
    isHorizontal: false,
  });

  const count = projects.length;
  const maxIndex = count - 1;

  // Measure card width (responsive) for translate math
  useEffect(() => {
    const measure = () => {
      const track = trackRef.current;
      if (!track) return;
      const firstCard = track.firstElementChild;
      if (firstCard) {
        const style = window.getComputedStyle(track);
        const gap = parseFloat(style.columnGap || style.gap || '0');
        setCardWidth(firstCard.offsetWidth + gap);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const clampActive = useCallback(
    (deltaCards) => {
      setActive((prev) => {
        const next = prev + deltaCards;
        if (next < 0) return 0;
        if (next > maxIndex) return maxIndex;
        return next;
      });
    },
    [maxIndex]
  );

  // Auto-advance
  useEffect(() => {
    if (paused || isDragging) return;
    const id = setInterval(() => {
      setActive((prev) => (prev + 1) % count);
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, isDragging, count]);

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    const track = trackRef.current;
    if (!track) return;
    setPaused(true);
    setIsDragging(true);
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      lastX: e.clientX,
      velocity: 0,
      pointerId: e.pointerId,
      decided: false,
      isHorizontal: false,
    };
    track.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (!isDragging) return;
    const ds = dragState.current;
    const dx = e.clientX - ds.startX;
    const dy = e.clientY - ds.startY;

    // Decide axis on first significant move
    if (!ds.decided) {
      if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
      ds.decided = true;
      ds.isHorizontal = Math.abs(dx) > Math.abs(dy);
    }
    if (!ds.isHorizontal) return;

    const instantVelocity = e.clientX - ds.lastX;
    ds.velocity = ds.velocity * 0.6 + instantVelocity * 0.4;
    ds.lastX = e.clientX;

    let next = dx;
    // Rubber-band at edges
    if ((active === 0 && dx > 0) || (active === maxIndex && dx < 0)) {
      next = dx * 0.35;
    }
    setDragOffset(next);
  };

  const finishDrag = (e) => {
    if (!isDragging) return;
    const ds = dragState.current;
    setIsDragging(false);
    setPaused(false);

    const track = trackRef.current;
    if (track && ds.pointerId !== null) {
      try { track.releasePointerCapture(ds.pointerId); } catch { /* noop */ }
    }

    if (!ds.decided || !ds.isHorizontal) {
      setDragOffset(0);
      return;
    }

    const deltaCards = Math.round(-dragOffset / Math.max(cardWidth, 1));
    // Momentum: if velocity is high, bias toward movement direction
    let final = deltaCards;
    if (Math.abs(ds.velocity) > 6 && deltaCards === 0) {
      final = ds.velocity > 0 ? -1 : 1;
    }
    clampActive(final);
    setDragOffset(0);
  };

  const goTo = (i) => {
    setPaused(true);
    setActive(Math.max(0, Math.min(maxIndex, i)));
    // Resume auto-advance after a beat
    setTimeout(() => setPaused(false), 1500);
  };

  const baseTranslate = -active * cardWidth;
  const totalTranslate = baseTranslate + dragOffset;

  return (
    <section id="portfolio" className="section-pad relative overflow-hidden">
      {/* Ambient glow */}
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-1/3 h-96 w-96 -translate-x-1/2 rounded-full bg-cyan/10 blur-[140px]" />

      <div className="container-x">
        <SectionHeading
          eyebrow="Selected work"
          title="Outcomes we're proud of."
          subtitle="A snapshot of platforms we've shipped — each one engineered for scale and measured by the metrics that matter to the business."
        />

        <div
          className="relative mt-16"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => !isDragging && setPaused(false)}
        >
          {/* Track */}
          <div className="mask-fade-x overflow-hidden">
            <div
              ref={trackRef}
              className="flex cursor-grab touch-pan-y select-none active:cursor-grabbing"
              style={{
                transform: `translate3d(${totalTranslate}px, 0, 0)`,
                transition: isDragging
                  ? 'none'
                  : 'transform 700ms cubic-bezier(0.16, 1, 0.3, 1)',
                columnGap: '1.5rem',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={finishDrag}
              onPointerCancel={finishDrag}
            >
              {projects.map((p, i) => (
                <article
                  key={p.id}
                  className="relative w-[85vw] shrink-0 sm:w-[560px] lg:w-[640px]"
                  style={{ transform: i === active ? 'scale(1)' : 'scale(0.94)', transition: 'transform 600ms cubic-bezier(0.16,1,0.3,1)' }}
                  aria-hidden={i !== active}
                >
                  <div className="card-surface overflow-hidden">
                    {/* Visual header */}
                    <div className={`relative h-56 overflow-hidden bg-gradient-to-br ${p.accent} sm:h-64`}>
                      <div
                        className="absolute inset-0 opacity-30"
                        style={{
                          backgroundImage:
                            'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)',
                          backgroundSize: '32px 32px',
                        }}
                      />
                      <div className="absolute inset-0 grid place-items-center">
                        <span className="font-heading text-5xl font-extrabold text-white/90 drop-shadow-lg">
                          {p.title.split(' ')[0]}
                        </span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-900/80 to-transparent p-5">
                        <span className="text-xs font-semibold uppercase tracking-widest text-cyan">
                          {p.category}
                        </span>
                      </div>
                    </div>

                    {/* Body */}
                    <div className="p-6 sm:p-8">
                      <h3 className="font-heading text-2xl font-bold text-white">{p.title}</h3>
                      <p className="mt-3 text-silver/80">{p.summary}</p>

                      <div className="mt-6 grid grid-cols-3 gap-3">
                        {p.metrics.map((m) => (
                          <div key={m.label} className="rounded-xl border border-white/5 bg-white/[0.03] p-3 text-center">
                            <div className="font-heading text-lg font-bold text-cyan sm:text-xl">{m.value}</div>
                            <div className="text-[11px] uppercase tracking-wide text-silver/60">{m.label}</div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex flex-wrap gap-2">
                        {p.tags.map((t) => (
                          <span key={t} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs font-medium text-silver-light/80">
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Controls */}
          <div className="mt-8 flex items-center justify-between gap-4">
            {/* Dots */}
            <div className="flex items-center gap-2">
              {projects.map((p, i) => (
                <button
                  key={p.id}
                  onClick={() => goTo(i)}
                  aria-label={`Go to project ${i + 1}`}
                  className="group relative h-2.5 overflow-hidden rounded-full bg-white/10 transition-all duration-300"
                  style={{ width: i === active ? 32 : 10 }}
                >
                  <span
                    className="absolute inset-y-0 left-0 bg-cyan"
                    style={{
                      width: i === active ? `${paused ? 0 : 100}%` : '0%',
                      transition: paused
                        ? 'none'
                        : i === active
                          ? `width ${AUTO_ADVANCE_MS}ms linear`
                          : 'width 300ms ease',
                    }}
                  />
                </button>
              ))}
            </div>

            {/* Prev / Next */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => goTo(active - 1)}
                disabled={active === 0}
                aria-label="Previous project"
                className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-all hover:border-cyan/60 hover:bg-cyan/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                  <path d="M12 4l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                onClick={() => goTo(active + 1)}
                disabled={active === maxIndex}
                aria-label="Next project"
                className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-white transition-all hover:border-cyan/60 hover:bg-cyan/10 disabled:cursor-not-allowed disabled:opacity-30"
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="none">
                  <path d="M8 4l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
